/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { InputField, Button } from "@/components";
import { FaIndianRupeeSign } from "react-icons/fa6";
import { useApi, useAlert, useRechargeDialog } from "@/";

const predefinedAmounts = [1000, 5000, 10000, 25000, 50000, 100000];
const defaultAmount = 1000;
const MIN_RECHARGE = 1000;

const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.body.appendChild(script);
  });
};

const RechargeWallet = ({ user }) => {
  const { showSuccess, showFailed, showCancelled, RechargeDialog } =
    useRechargeDialog();
  const [amount, setAmount] = useState(defaultAmount.toString());
  const [isLoading, setIsLoading] = useState(false);
  const razorpayKey = import.meta.env.VITE_RAZORPAY_KEY; // Load from .env
  const { alert, AlertDialog } = useAlert();
  const { callApi } = useApi();

  const showCancelledRef = useRef(showCancelled);
  const showFailedRef = useRef(showFailed);

  // Update refs when values change
  useEffect(() => {
    showCancelledRef.current = showCancelled;
    showFailedRef.current = showFailed;
  }, [showCancelled, showFailed]);

  const handleAmountClick = (value) => setAmount(value.toString());

  const handleAmountChange = (val) => {
    // Allow only up to 2 decimal places
    if (val === "" || /^\d*(\.\d{0,2})?$/.test(val)) {
      setAmount(val);
    }
  };

  const handlePayment = async (amt) => {
    if (!razorpayKey) {
      alert("Something went wrong, please try again later");
      return;
    }
    setIsLoading(true);
    try {
      const res = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js",
      );
      if (!res) throw new Error("Razorpay SDK failed to load.");

      // Request order from backend
      const response = await callApi("/payment/orders", "POST", {
        amount: amt,
      });

      if (!response.success) {
        console.log("response", response);
        alert(`${response.error}`);
        setIsLoading(false);
        return;
      }

      console.log("Order Data:", response.data);
      const {
        orderId: order_id,
        amount,
        currency,
        receipt: IccOrderId,
      } = response.data;
      //alert("Payment Initiated! amount: " + amount + " currency: " + currency + " order_id: " + order_id);
      const name = user?.name || "Guest";
      const email = user?.email || "example@mail.com";
      const phone = user?.kycDetails?.personalDetails?.phone1 || "0000000000";

      const options = {
        key: razorpayKey, // Load from .env
        amount: amount,
        currency,
        name: "Indian Courier Company",
        description: "Wallet Recharge",
        order_id,
        handler: async function (response) {
          try {
            const { success, message } = await callApi(
              "/payment/verify",
              "POST",
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                IccOrderId,
              },
            );
            if (success) {
              console.log(`Payment successful! :- ${message}`);
              showSuccess();
              setIsLoading(false);
            } else {
              console.error(`Payment verification failed! :- ${message}`);
              showFailedRef.current(); // Use ref
              setIsLoading(false);
            }
          } catch (error) {
            console.log(
              `Payment verification failed! ${
                error.error.message || error.message || error
              }`,
            );
            showFailedRef.current(); // Use ref
            setIsLoading(false);
          }
        },
        prefill: {
          name,
          email,
          contact: phone,
        },
        theme: { color: "#9dd257" },
      };

      const rzp = new window.Razorpay(options);

      // Use stable ref callbacks for event listeners
      rzp.on("payment.failed", (response) => {
        console.error("Payment Failed:", response.error);
        showFailedRef.current();
        setIsLoading(false);
      });

      rzp.on("close", () => {
        console.log("Payment modal closed without completion");
        showCancelledRef.current(); // Use ref
        setIsLoading(false);
      });

      rzp.open();
    } catch (error) {
      console.error("Payment Error:", error);
      alert(error.message);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (isNaN(amt) || amt < MIN_RECHARGE) {
      alert(`Minimum recharge amount is ${MIN_RECHARGE} Rupees.`);
      return;
    }
    handlePayment(amt);
  };

  return (
    <div className="flex flex-col gap-4 w-fit p-4 border border-black/15 rounded-xl bg-secondary/5">
      <h2 className="text-headline4 lg:text-headline3 font-nunito font-semibold text-secondary/95">
        Recharge Wallet
      </h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
        <div className="flex flex-col gap-1">
          <InputField
            label="Amount for Recharge"
            type="number"
            step="0.01"
            placeholder="Enter amount. Eg. 1,000"
            value={amount}
            onChange={handleAmountChange}
            disabled={isLoading}
          />
          {amount !== "" && Number(amount) < MIN_RECHARGE ? (
            <p className="text-xs text-red-500 font-nunito ml-2 mt-0.5 flex items-center gap-1">
              ⚠️ Minimum recharge amount is ₹{MIN_RECHARGE}
            </p>
          ) : (
            <p className="text-xs text-secondary/60 font-nunito ml-2 mt-0.5">
              Minimum recharge amount is ₹{MIN_RECHARGE}
            </p>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {predefinedAmounts.map((value) => (
            <Button
              type="button"
              key={value}
              variant="outline"
              size="md"
              onClick={() => handleAmountClick(value)}
              disabled={isLoading}
            >
              <FaIndianRupeeSign />
              {value}
            </Button>
          ))}
        </div>
        <Button
          variant="default"
          type="submit"
          size="lg"
          className="w-full"
          disabled={isLoading || (amount !== "" && Number(amount) < MIN_RECHARGE)}
          isLoading={isLoading}
          loadingText="Processing..."
        >
          Recharge
        </Button>
      </form>

      <AlertDialog />
      <RechargeDialog />
    </div>
  );
};

export default RechargeWallet;
